import {inject, Injectable} from './di';
import {BehaviorSubject} from 'rxjs';

import {Logger} from './logger';
import {ConfigService} from './config/config-service';
import {AppInitializeService} from './app-initialize-service';

@Injectable()
export class TouchInterfaceService implements AppInitializeService {
  private readonly logger = inject(Logger);
  private readonly configService = inject(ConfigService);

  private readonly delay: number = 300;
  private readonly maxMove: number = 10;

  private scalingFactor: number = 1;
  private lastTime: number = 0; // время последнего «активационного» события (tap/click)
  private singleTimer: ReturnType<typeof setTimeout> | null = null;
  private touchStartX: number = 0;
  private touchStartY: number = 0;
  private moved: boolean = false;

  public isTouchable$ = new BehaviorSubject<boolean>(false);

  public init(): boolean {
    try {
      this.logger.debug('[TouchInterfaceService] TouchInterfaceService initialization started');

      const scalingFactor: number | undefined = this.configService.config.services?.touchInterface?.scalingFactor;
      if (scalingFactor !== undefined) {
        this.scalingFactor = scalingFactor;
        this.logger.debug(`[TouchInterfaceService] scalingFactor was changed to ${scalingFactor}`);
      }

      // Add touch event listeners
      document.addEventListener('touchstart', this.handleTouchStart.bind(this), {passive: true});
      document.addEventListener('touchmove', this.handleTouchMove.bind(this), {passive: true});
      document.addEventListener('touchend', this.handleTouchEnd.bind(this));

      this.logger.debug('[TouchInterfaceService] Touch event listeners added');

      this.logger.debug('[TouchInterfaceService] TouchInterfaceService initialization completed successfully');

      window.touchInterface = this;

      this.isTouchable$.subscribe((isTouchable) => {
        if (!isTouchable) {
          return;
        }

        document.body.classList.add('touchable');
      })
      return true;
    } catch (err) {
      this.logger.error('[TouchInterfaceService] TouchInterfaceService initialization failed', err);
      return false;
    }
  }

  private distance = (x1: number, y1: number, x2: number, y2: number): number => Math.hypot(x1 - x2, y1 - y2)

  /**
   * Получает элемент по координатам события
   * @param event Touch или Mouse событие
   * @returns Элемент в указанных координатах или null
   */
  private getElementFromCoordinates(event: TouchEvent | MouseEvent): HTMLElement | null {
    try {
      let x: number;
      let y: number;

      if (event instanceof TouchEvent) {
        // Для TouchEvent используем changedTouches (доступны в touchend) или touches
        const touch = event.changedTouches?.[0] || event.touches?.[0];
        if (!touch) {
          return null;
        }
        x = touch.clientX;
        y = touch.clientY;
      } else {
        // Для MouseEvent используем clientX и clientY
        x = event.clientX;
        y = event.clientY;
      }

      x = Math.round(x / this.scalingFactor);
      y = Math.round(y / this.scalingFactor);

      this.logger.debug('[TouchInterfaceService] touch coordinates', `x: ${x}`, `y: ${y}`);

      const element = document.elementFromPoint(x, y);
      return element as HTMLElement | null;
    } catch (err) {
      this.logger.error('[TouchInterfaceService] Error getting element from coordinates', err);
      return null;
    }
  }

  private processActivation(event: TouchEvent | MouseEvent, now: number) {
    this.isTouchable$.next(true);
    const diff = now - this.lastTime;

    // двойное срабатывание
    if (diff > 0 && diff < this.delay) {
      if (this.singleTimer) {
        clearTimeout(this.singleTimer);
        this.singleTimer = null;
      }
      this.lastTime = 0;
      this.doubleClick(event);
      return;
    }

    // отложенный одиночный
    if (this.singleTimer) clearTimeout(this.singleTimer);
    this.singleTimer = setTimeout(() => {
      this.singleTimer = null;
      this.click(event);
    }, this.delay);

    this.lastTime = now;
  }

  /**
   * Handle touch events and find closest selectable element
   * @param event Touch or mouse event
   */
  private click(event: TouchEvent | MouseEvent): void {
    try {
      const target = this.getElementFromCoordinates(event);
      this.logger.debug('[TouchInterfaceService] Click {target}', target);
      if (!target) {
        return;
      }

      this.logger.debug(
        '[TouchInterfaceService] Click {target} attributes',
        target.attributes,
        `id: ${target?.id}`,
        `class: ${target?.className}`
      );

      const selectableElement = target.closest('[clickable]');
      this.logger.debug('[TouchInterfaceService] Click {selectableElement}', selectableElement);
      if (!selectableElement) {
        return;
      }

      $(selectableElement).trigger(event_link);
    } catch (err) {
      this.logger.error('[TouchInterfaceService] Error in click', err);
    }
  }

  private doubleClick(event: TouchEvent | MouseEvent): void {
    try {
      const target = this.getElementFromCoordinates(event);
      if (!target) {
        return;
      }

      const selectableElement = target.closest('[clickable]');
      if (!selectableElement) {
        return;
      }

      $(selectableElement).trigger(event_double_click);
    } catch (err) {
      this.logger.error('[TouchInterfaceService] Error in double click', err);
    }
  }

  private handleTouchStart(event: TouchEvent): void {
    this.logger.debug('[TouchInterfaceService] handleTouchStart fired', event);

    const touch = event.touches && event.touches[0];
    if (!touch) return;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.moved = false;
  }

  private handleTouchMove(event: TouchEvent): void {
    this.logger.debug('[TouchInterfaceService] handleTouchMove fired', event);

    const touch = event.touches && event.touches[0];
    if (!touch) return;
    if (this.distance(touch.clientX, touch.clientY, this.touchStartX, this.touchStartY) > this.maxMove) {
      this.moved = true;
    }
  }

  private handleTouchEnd(event: TouchEvent): void {
    this.logger.debug('[TouchInterfaceService] handleTouchEnd fired', event);

    if (this.moved) return;
    const now = Date.now();
    this.processActivation(event, now);
  }
}

export type TouchInterfaceType = TouchInterfaceService;
